import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import record, { flatten, deepen } from '../../versa/record.js';
import { notify, ask } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';
import { splitIdsByCompany, resolvePrinterDeviceId } from '../../versa/misc.js';
import {
	resolveInputCancelReasonId,
	hydrateInputCancelReasons,
	fetchInputCancelReasonsSilent,
} from '../utils/input-cancel-reason.js';
import pdf from '../../versa/pdf.js';
import view from '../views/inputs.js';
import form from '../forms/inputs.js';
import inventory_form from '../forms/inputs-inventory.js';
import putaway_form from '../forms/inputs-putaway.js';
import buildCreateInvoicesForm from '../forms/create-invoices.js';
import print_labels_form from '../forms/inputs-labels.js';
import reverse_form from '../forms/inputs-reverse.js';
import print_complete_form from '../forms/inputs-print-complete-report.js';
import print_documents_list_form from '../forms/inputs-print-documents-list.js';
import export_csv_form from '../forms/inputs-export-csv.js';
import * as types from '../types.js';

const inputs = function()
{

	let inputs_crud = crud.create({
		async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options)
		{
			try {
				const raw = localStorage.getItem('inputs.setup');
				if (raw) {
					const s = JSON.parse(raw);
					if (!s.in_grid_dock_schedule_v1) {
						const cols = String(s.columns || this.default_columns || options?.default_columns || '')
							.split(',')
							.map((c) => c.trim())
							.filter(Boolean);
						if (!cols.includes('29'))
							cols.push('29');
						s.columns = cols.join(',');
						s.in_grid_dock_schedule_v1 = true;
						localStorage.setItem('inputs.setup', JSON.stringify(s));
					}
				}
			} catch (_) { /* setup opcional */ }
			return crud.run.call(this, module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options);
		},
		async loadScopedLocationsToDb(filterOverride = null)
		{
			const saved = this.filter;
			try
			{
				if (filterOverride != null)
					this.filter = await resolveFilterLabelsToIds(filterOverride, this.fieldspecs);
				const q = await this.queryFilteredForLocations();
				if (!/[?&]filter=/.test(q))
					return;
				const data = await api.call(api.list_locations, q);
				await db.set('locations', Array.isArray(data) ? data : []);
			}
			finally
			{
				if (filterOverride != null)
					this.filter = saved;
			}
		},
		async safeGetJson(endpointFn, fallbackPath)
		{
			if (typeof endpointFn === 'function')
				return await api.call(endpointFn);
			const endpoint = `${api.HOST}${fallbackPath}`;
			const response = await fetch(endpoint, { method: 'GET', credentials: 'include' });
			if (!response.ok)
				throw new Error(`Falha ao consultar API (${response.status})`);
			const contentType = (response.headers.get('content-type') || '').toLowerCase();
			if (!contentType.includes('application/json'))
				throw new Error('Resposta inválida da API (esperado JSON).');
			return await response.json();
		},
		async loadCompanyProductsToDb(company_id)
		{
			if (company_id == null || company_id === '')
				return;
			const qs = new URLSearchParams({
				filter: `company_id*eq*${company_id}`,
				sort: '2',
				limit: '0',
			});
			const data = await this.safeGetJson(
				typeof api.list_products === 'function' ? (() => api.list_products('?' + qs.toString())) : null,
				`/products?${qs.toString()}`
			);
			if (Array.isArray(data))
				await db.set('products', data);
		},
		async loadLocationsForCompanyToDb(company_id, warehouse_id = null)
		{
			if (company_id == null || company_id === '')
				return;
			const parts = [`company_id*eq*${company_id}`];
			if (warehouse_id != null && warehouse_id !== '')
				parts.push(`warehouse_id*eq*${warehouse_id}`);
			const qs = new URLSearchParams({
				filter: parts.join(','),
				sort: '4',
				limit: '0',
			});
			const data = await this.safeGetJson(
				typeof api.list_locations === 'function' ? (() => api.list_locations('?' + qs.toString())) : null,
				`/locations?${qs.toString()}`
			);
			if (Array.isArray(data))
				await db.set('locations', data);
		},
		async loadBaseData()
		{
			const q = this.queryAll();
			await Promise.all([
				api.call(api.list_companies, q).then((data) => data && db.set('companies', data)).catch((err) => notify(err?.message || String(err))),
				api.call(api.list_warehouses, q).then((data) => data && db.set('warehouses', data)).catch((err) => notify(err?.message || String(err))),
				api.call(api.list_devices, q).then((data) => data && db.set('devices', data)).catch(() => {}),
				api.call(api.list_programs, q).then((data) => data && db.set('programs', data)).catch(() => {}),
				api.call(api.list_units, q).then((data) => data && db.set('units', data)).catch((err) => notify(err?.message || String(err))),
			]);
			await this.loadScopedLocationsToDb();
		},
		async queryData(query)
		{
			return await api.call(api.list_inputs, query);
		},
		async loadRecord(id)
		{
			const [company_id, input_id] = JSON.parse(id);
			const _record = await this.safeGetJson(
				typeof api.select_input === 'function' ? (() => api.select_input(company_id, input_id)) : null,
				`/companies/${company_id}/inputs/${input_id}`
			);
			if (!_record)
				return null;
			try
			{
				await this.loadCompanyProductsToDb(_record.company_id);
			}
			catch (_) { /* datalist de produtos pode ficar vazio; modal ainda abre */ }
			try
			{
				const whCo = _record.warehouse_company_id ?? _record.company_id;
				await this.loadLocationsForCompanyToDb(whCo, _record.warehouse_id);
			}
			catch (_) { /* posições recarregam ao mudar armazém no modal */ }
			if (_record.order_type === 'I' && _record.order_id)
			{
				try
				{
					const order = await api.call(api.select_input_order, _record.company_id, _record.order_id);
					if (order)
					{
						const count = Number(order.payment_installment_count);
						let prazos = order.payment_prazos;
						if (typeof prazos === 'string') {
							try { prazos = JSON.parse(prazos); } catch (_) { prazos = null; }
						}
						if (Number.isFinite(count) && count > 0 && Array.isArray(prazos) && prazos.length)
						{
							const parts = prazos.slice(0, count).map((d, i) => `${String(i + 1).padStart(2, '0')}:${d}d`);
							_record.order_payment_summary = `${count} parc. — ${parts.join(' · ')}`;
						}
					}
				}
				catch (_) { /* bloco cond. pag. fica oculto */ }
			}
			try
			{
				const isCanceled = Number(_record.status) === 80;
				await hydrateInputCancelReasons(_record.company_id, isCanceled);
			}
			catch (_) { /* datalist motivo pode falhar; modal abre */ }
			return flatten(_record, 'items');
		},
		async editFilter() {
			await this.loadScopedLocationsToDb(this.filter);
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) => { await this.loadScopedLocationsToDb(formData); },
			});
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			this.filterExplicitlyApplied = true;
			await this.loadScopedLocationsToDb(this.filter);
			await this.loadPage(1);
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'items');
			let _record = {
				order_type: data.order_type,
				order_id: Number(data.order_id),
				warehouse_company_id: data.warehouse_company_id,
				warehouse_id: Number(data.warehouse_id),
				location_id: Number(data.location_id),
				program_id: (data.program_id != null && data.program_id !== '' && Number(data.program_id) >= 0) ? Number(data.program_id) : null,
				invoice_number: data.invoice_number,
				invoice_series: data.invoice_series || null,
				invoice_xml: data.invoice_xml,
				volumes: Number(data.volumes),
				weight: Number(data.weight),
				status: Number(data.status),
				items: data.items
				.filter(item => item && (item.product_id != null && item.product_id !== '') && Number(item.product_id) > 0)
				.map(item => {
					const pp = item.product_packaging_id;
					const packaging_id = (pp != null && pp !== '' && Number(pp) > 0) ? Number(pp) : null;
					return {
						product_id: Number(item.product_id),
						unit_id: Number(item.unit_id),
						quantity: Number(item.quantity),
						unit_price: Number(item.unit_price),
						product_packaging_id: packaging_id,
						serial: (item.serial && String(item.serial).trim()) ? String(item.serial).trim().slice(0, 20) : null,
					};
				})
			};
			if (Number(data.status) === 80)
			{
				const rid = await resolveInputCancelReasonId(data.cancel_reason_id, data.company_id);
				if (!rid || !Number.isFinite(rid))
					throw new Error('Informe o Motivo de cancelamento ao definir a Entrada como Cancelada.');
				_record.cancel_reason_id = rid;
			}
			else
			{
				_record.cancel_reason_id = null;
			}
			if (old_data)
				return await api.call(api.update_input, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_input, data.company_id, _record);
		},
		async inventory()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'lots');
				let _record = {
					warehouse_company_id: data.warehouse_company_id,
					warehouse_id: Number(data.warehouse_id),
					location_id: Number(data.location_id),
					program_id: (data.program_id != null && data.program_id !== '') ? Number(data.program_id) : null,
					lots: data.lots.map(lot => {
						const pp = lot.product_packaging_id;
						const packaging_id = (pp != null && pp !== '' && Number(pp) > 0) ? Number(pp) : null;
						const rawId = lot.id;
						const lotId = rawId != null && rawId !== '' ? Number(rawId) : null;
						return {
							id: Number.isFinite(lotId) && lotId > 0 ? lotId : null,
							product_id: Number(lot.product_id),
							product_packaging_id: packaging_id,
							lot_number: lot.lot_number,
							serial: (lot.serial && String(lot.serial).trim()) ? String(lot.serial).trim().slice(0, 20) : null,
							manufacture_date: lot.manufacture_date,
							expiration_date: lot.expiration_date,
							quantity: Number(lot.quantity),
							status: Number(lot.status),
							is_blocked: lot.is_blocked == '1'	// Can be: undefined, '0', '1'
						};
					})
				};
				if (await api.call(api.inventory_input, data.company_id, data.input_id, _record))
				{
					api.call(api.list_lots, this.queryAll()).then((data) => data && db.set('lots', data)).catch((err) => notify(err?.message || String(err)));
					this.loadPage();	// Show updated input status
					return true;
				}
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para conferência', 'Conferência de Entrada');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			// Alinhado a entities/input_.py: inventory() exige status >= ST_RECEIVED (30); >= ST_VERIFIED (50) já concluiu.
			const INPUT_ST_RECEIVED = 30;
			const INPUT_ST_VERIFIED = 50;
			const st = Number(input.status);
			if (st < INPUT_ST_RECEIVED)
				return notify(
					'Conclua o recebimento desta entrada antes da conferência (status "Recebida" ou superior, por exemplo após registrar data de recebimento na edição da entrada).',
					'Conferência de Entrada'
				);
			if (st >= INPUT_ST_VERIFIED)
				return notify('Esta entrada já foi conferida.', 'Conferência de Entrada');
			let lots = await api.call(api.list_lots, `?filter=company_id*eq*${input.company_id},input_id*eq*${input.id}&limit=0`);
			if ((!lots || lots.length === 0)) {
				const seedItems = (input.items && input.items.length)
					? input.items
					: null;
				if (seedItems) {
					lots = seedItems
						.filter((it) => it && it.product_id)
						.map((it) => ({
							id: null,
							product_id: it.product_id,
							product_packaging_id: it.product_packaging_id || null,
							lot_number: '',
							serial: it.serial || '',
							manufacture_date: '',
							expiration_date: '',
							quantity: it.quantity || '',
							status: 0,
							is_blocked: false,
						}));
				} else if (input.order_id) {
					try {
						const order = await api.call(api.select_input_order, input.company_id, input.order_id);
						const items = order?.items || [];
						lots = items
							.filter((it) => it && it.product_id)
							.map((it) => ({
								id: null,
								product_id: it.product_id,
								product_packaging_id: null,
								lot_number: '',
								serial: it.serial || '',
								manufacture_date: '',
								expiration_date: '',
								quantity: it.quantity || '',
								status: 0,
								is_blocked: false,
							}));
					} catch (_) {
						/* pedido opcional — conferência segue em branco */
					}
				}
			}
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'warehouse_company_id': input.warehouse_company_id,
				'warehouse_id': input.warehouse_id,
				'location_id': input.location_id,
				'program_id': input.program_id ?? '',
				'lots': (lots || []).map(lot => { lot.is_blocked = Boolean(lot.status & 0b1000); return lot; })
			};
			await api.call(api.list_programs, this.queryAll()).then((data) => data && db.set('programs', data)).catch(() => {});
			record.edit(inventory_form, sendRequest, flatten(_record, 'lots'));
		},
		async putAway()
		{
			const fmtExp = (d) =>
			{
				if (d == null || d === '') return '';
				const s = String(d);
				return s.length >= 10 ? s.slice(0, 10) : s;
			};
			/** Uma linha por lote na posição de recebimento; quantidades somadas se houver mais de um stock_id. */
			const buildPutawayRows = (stocks, input) =>
			{
				const stagingId = Number(input.location_id);
				let cand = (stocks || []).filter((s) =>
					Number(s.location_id) === stagingId && Number(s.quantity_available) > 0
				);
				if (cand.length === 0) {
					cand = (stocks || []).filter((s) => Number(s.quantity_available) > 0);
				}
				const byLot = new Map();
				for (const s of cand) {
					const lid = Number(s.lot_id);
					if (!byLot.has(lid)) {
						byLot.set(lid, {
							lot_id: lid,
							lot_number: s.lot_number || '',
							sku: s.sku || '',
							product_name: s.product_name || '',
							expiration_date: fmtExp(s.expiration_date),
							location_id: '',
							quantity: 0,
						});
					}
					const row = byLot.get(lid);
					row.quantity += Number(s.quantity_available || 0);
					if (!row.lot_number && s.lot_number) row.lot_number = s.lot_number;
					if (!row.sku && s.sku) row.sku = s.sku;
					if (!row.product_name && s.product_name) row.product_name = s.product_name;
					if (!row.expiration_date && s.expiration_date) row.expiration_date = fmtExp(s.expiration_date);
				}
				return [...byLot.values()];
			};
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'puts');
				const puts = (data.puts || []).filter((put) =>
					put &&
					put.lot_id != null && put.lot_id !== '' &&
					put.location_id != null && put.location_id !== '' &&
					Number(put.quantity) > 0
				).map((put) => ({
					lot_id: Number(put.lot_id),
					location_id: Number(put.location_id),
					quantity: Number(put.quantity),
				}));
				if (puts.length === 0) {
					notify('Informe ao menos uma linha com posição de destino e quantidade.', 'Endereçamento de Lotes');
					return false;
				}
				const _record = { puts };
				if (await api.call(api.putaway_input, data.company_id, data.input_id, _record))
				{
					this.loadPage();
					return true;
				}
			};
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para endereçamento', 'Endereçamento de Lotes');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			let stocks = await api.call(api.list_stocks_by_input, input.company_id, input.id);
			const putawayRows = buildPutawayRows(stocks, input);
			if (putawayRows.length === 0) {
				return notify(
					'Não há estoque disponível na posição de recebimento desta entrada. Conclua a conferência ou verifique a posição da entrada.',
					'Endereçamento de Lotes'
				);
			}
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'warehouse_company_id': input.warehouse_company_id,
				'warehouse_id': input.warehouse_id,
				'stocks': putawayRows,
			};
			record.edit(putaway_form, sendRequest, flatten(_record, 'stocks'));
		},
		printInputs()
		{
			if (!this.ensureSelection('Listagem de Entradas'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Listagem de Entradas');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_inputs, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Listagem de Entradas', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async exportListCsv()
		{
			const crud = this;
			await record.edit(export_csv_form, async () =>
			{
				const box = document.querySelector(`${export_csv_form.form.selector} #use_list_filter`);
				const useList = box ? box.checked : true;
				const payload = {};
				if (useList)
				{
					const params = new URLSearchParams();
					await crud.addFilter(params);
					crud.addSort(params);
					const f = params.get('filter');
					const s = params.get('sort');
					if (f)
						payload.filter = f;
					if (s)
						payload.sort = s;
				}
				try
				{
					const resp = await api.call(api.export_inputs_csv, payload);
					if (resp?.csv)
					{
						downloadInputsCompleteFile(resp.csv, resp.filename || 'entradas.csv', 'text/csv;charset=utf-8');
						notify('Download iniciado.', 'Exportar listagem (CSV)');
					}
					else
						notify('Não foi possível gerar o arquivo.', 'Aviso');
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Erro');
				}
			}, {}, false);
		},
		printInputSKUs()
		{
			if (!this.ensureSelection('Listagem de Nota Fiscal'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Cruzamento de Nota Fiscal');
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_input_skus, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas na listagem?`, 'Listagem de Nota Fiscal', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printXML()
		{
			if (!this.ensureSelection('Espelho da Nota Fiscal'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Espelho da Nota Fiscal');
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_input_xml, { 'ids': ids }).then(showResult);
			}
			ask(`Gerar espelho da nota de ${this.getCount()} entradas?`, 'Espelho da Nota Fiscal', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printPutAwayOrder()
		{
			if (!this.ensureSelection('Ordem de Endereçamento'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Ordem de Endereçamento');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_putaway, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Ordem de Endereçamento', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async printLabels()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'lots');
				const device_id = resolvePrinterDeviceId(data.device_id, '#input-labels #device_id');
				if (device_id == null)
				{
					notify('Selecione a impressora na lista (clique na opção; só o texto no campo não basta).', 'Atenção');
					return false;
				}
				let _record = {
					device_company_id: data.device_company_id,
					device_id,
					return_zpl: !!data.use_local_agent,
					lots: data.lots.map(lot => ({ 
						lot_id: Number(lot.id),
						labels: Number(lot.labels)
					}))
				};
				const result = await api.call(api.print_input_labels, data.company_id, data.input_id, _record);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para impressão', 'Etiquetas de Lotes');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			// #MT-76C5334 — reuso: lots.input_id fica na 1ª entrada; listar via I→L.
			let lots = await api.call(api.list_input_conference_lots, input.company_id, input.id);
			if (!lots || !lots.length)
				return notify('Esta entrada não tem lotes conferidos para etiquetar.', 'Etiquetas de Lotes');
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'lots': lots.map(lot => { lot.labels = 1; return lot; })
			};
			record.edit(print_labels_form, sendRequest, flatten(_record, 'lots'));
		},
		printCosts()
		{
			if (!this.ensureSelection('Relatório de Custos'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Relatório de Custos');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_costs, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Relatório de Custos', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printConference()
		{
			if (!this.ensureSelection('Relatório de Conferência'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Conferência');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_conference, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório de conferência?`, 'Relatório de Conferência', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printDetails()
		{
			if (!this.ensureSelection('Entrada Detalhada'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Entrada detalhada');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_details, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório detalhado?`, 'Entrada Detalhada', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		createInvoices()
		{
			if (!this.ensureSelection('Gerar Notas'))
				return;
			const showResult = (resp) =>
			{
				this.clearGridSelection();
				notify(`Notas criadas: ${resp?.count || 0}`, 'Resultado');
				fs.destroy();
			}
			const sendRequest = async (data) =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.create_invoices, Object.assign(data, { 'io_type': 'I', 'ids': ids })).then(showResult);
			}
			let fs = formset.create(buildCreateInvoicesForm(true), sendRequest);
			fs.init = (() =>
			{
				const n = this.getCount();
				document.querySelector(`${fs.form.selector} #count`).innerHTML = n;
				const noun = document.querySelector(`${fs.form.selector} #io-noun`);
				if (noun)
					noun.textContent = n === 1 ? 'entrada' : 'entradas';
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		async reverseInput()
		{
			const sendRequest = async (data) =>
			{
				let resp = await api.call(api.reverse_input, data.company_id, data.input_id, { status: Number(data.status) });
				if (resp)
					this.loadPage();	// Show updated status
				return resp;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para estornar', 'Estorno de Entrada');
			record.edit(reverse_form, sendRequest, { company_id: ids[0][0], input_id: ids[0][1] });
		},

		/** #MT-8412900 — Cancelar Entradas Criadas em lote (checkbox + um motivo). */
		async cancelBatch()
		{
			const selectedIds = await this.getSelectedIds(['company_id', 'id']);
			if (!selectedIds?.length)
			{
				notify('Marque na grade as Entradas Criadas que deseja cancelar.', 'Cancelar em lote');
				return;
			}
			const companies = [...new Set(selectedIds.map(([c]) => String(c)))];
			if (companies.length !== 1)
			{
				notify('Selecione Entradas de uma única empresa por vez.', 'Cancelar em lote');
				return;
			}
			const company_id = companies[0];
			const ids = selectedIds.map(([, id]) => Number(id)).filter((n) => Number.isInteger(n) && n > 0);
			if (!ids.length)
			{
				notify('Nenhuma Entrada válida selecionada.', 'Cancelar em lote');
				return;
			}

			let reasons = [];
			try
			{
				const rows = await fetchInputCancelReasonsSilent(company_id, false);
				reasons = Array.isArray(rows) ? rows.filter((r) => r && !r.is_disabled) : [];
			}
			catch (e)
			{
				notify(e?.message || String(e), 'Erro ao carregar motivos');
				return;
			}
			if (!reasons.length)
			{
				notify(
					'Não há motivos de cancelamento ativos para esta empresa. Cadastre em Catálogo → Motivos Cancelamento Entrada.',
					'Cancelar em lote'
				);
				return;
			}

			const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (ch) => (
				{ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
			));
			const options = reasons
				.map((r) => `<option value="${esc(r.id)}">${esc(r.name)}</option>`)
				.join('');
			const total = ids.length;
			const html = `
				<div class="msgbox" style="min-width:380px;max-width:560px">
					<div class="mb-title">Cancelar Entradas em lote</div>
					<div class="mb-text">
						<p style="margin:0 0 0.75rem">
							Confirma o cancelamento de <strong>${total} entrada${total !== 1 ? 's' : ''}</strong> selecionada${total !== 1 ? 's' : ''}?
						</p>
						<p style="margin:0 0 0.75rem;color:#888;font-size:0.92em">
							Somente Entradas com status <strong>Criada</strong> serão canceladas.
							As demais serão ignoradas. Um único motivo vale para todo o lote.
						</p>
						<label style="display:block;margin:0.5rem 0 0.25rem">Motivo cancelamento<sup>*</sup></label>
						<select id="cancel_reason_id" required style="width:100%;box-sizing:border-box;min-height:2rem">
							<option value="">Selecione…</option>
							${options}
						</select>
					</div>
					<button type="button" class="button mtl bn_N">Voltar</button>
					<button type="button" class="button mtl bn_Y">Cancelar entradas</button>
				</div>
			`;

			const crudRef = this;
			const dlg = dialog.create(html, async (key) => {
				if (key === 'Escape' || key === 'N')
				{
					dlg.destroy();
					return;
				}
				if (key !== 'Y')
					return;
				const sel = dlg.getNode()?.querySelector('#cancel_reason_id');
				const cancel_reason_id = sel?.value != null ? String(sel.value).trim() : '';
				if (!cancel_reason_id)
				{
					notify('Selecione o Motivo de cancelamento.', 'Cancelar em lote');
					return;
				}
				dlg.destroy();
				try
				{
					const result = await api.call(api.cancel_inputs_batch, company_id, {
						ids,
						cancel_reason_id: Number(cancel_reason_id),
					});
					const canceled = Number(result?.canceled || 0);
					const skipped = Number(result?.skipped || 0);
					const failed = Number(result?.failed || 0);
					if (canceled > 0)
					{
						notify(
							`${canceled} entrada${canceled !== 1 ? 's' : ''} cancelada${canceled !== 1 ? 's' : ''}.` +
							(skipped > 0 ? ` ${skipped} ignorada${skipped !== 1 ? 's' : ''} (não estavam Criadas).` : '') +
							(failed > 0 ? ` ${failed} com erro.` : ''),
							'Cancelar em lote'
						);
						crudRef.loadPage?.();
					}
					else
					{
						const firstFail = (result?.failed_items || [])[0]?.error
							|| (result?.skipped_items || [])[0]?.error
							|| 'Nenhuma Entrada Criada foi cancelada. Confira a seleção e o status.';
						notify(firstFail, 'Cancelar em lote');
					}
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Erro ao cancelar em lote');
				}
			}, /Escape|N|Y/);
		},
	});
	
	// Index = ordem 1-based das colunas do SELECT em repos/inputs.SQL_BASE_SELECT (verified_at/by, program_id)
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Entrada', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador da entrada' },
		{ index: 4, field: 'order_id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'Número do pedido vinculado' },
		{ index: 3, field: 'order_type', label: 'Origem', types: [types.opt_order_type()], placeholder: '', title: 'Origem do pedido vinculado' },
		{ index: 13, field: 'invoice_number', label: 'Nota', types: [], placeholder: '', title: 'Número da nota fiscal' },
		{ index: 14, field: 'invoice_xml', label: 'XML', types: [], placeholder: '', title: 'XML da nota fiscal' },
		{ index: 5, field: 'created_on', label: 'Criação', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da criação da entrada' },
		{ index: 6, field: 'received_on', label: 'Recebimento', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora de recebimento da mercadoria' },
		{ index: 9, field: 'warehouse_company_id', label: 'Destinatária', types: [types.datalist()], placeholder: '', title: 'Empresa do armazém de recebimento', store: 'companies', labels: ['nick'] },
		{ index: 10, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém de recebimento', store: 'warehouses', labels: ['name'] },
		{ index: 12, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa da entrada', store: 'programs', labels: ['name'] },
		{ index: 11, field: 'location_id', label: 'Posição', types: [types.datalist()], placeholder: '', title: 'Posição no armazém', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 15, field: 'volumes', label: 'Volumes', types: [types.number(0, 2)], placeholder: '0', title: 'Número de volumes' },
		{ index: 16, field: 'weight', label: 'Peso', types: [types.number(3)], placeholder: '0,000', title: 'Peso total dos volumes' },
		{ index: 17, field: 'status', label: 'Status', types: [types.opt_input_status(), types.datalist()], placeholder: '', title: 'Status da entrada' },
		{ index: 29, field: 'dock_schedule_status', label: 'Agendamento', types: [], placeholder: '', title: 'Status do agendamento de doca do Pedido vinculado (#MT-0510946)', computed: true },
		{ index: 18, field: 'completed_at', label: 'Conclusão', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora em que a entrada foi concluída (endereçamento)' },
		{ index: 20, field: 'lines_packaging_summary', label: 'Embalagem (itens)', types: [], placeholder: '', title: 'Resumo das embalagens informadas nas linhas da entrada' },
		{ index: 21, field: 'lines_manufacturer_summary', label: 'Fabricante (itens)', types: [], placeholder: '', title: 'Resumo dos fabricantes das embalagens nas linhas da entrada' },
		{ index: 22, field: 'lines_sku_summary', label: 'SKU (itens)', types: [], placeholder: 'trecho do SKU', title: 'Busca nos SKUs das linhas da entrada (operador Contém recomendado)' },
		{ index: 23, field: 'lines_product_summary', label: 'Produto (itens)', types: [], placeholder: 'nome do produto', title: 'Busca nos nomes dos produtos das linhas (operador Contém recomendado)' },
		{ index: 26, field: 'cancel_reason_name', label: 'Motivo&nbsp;cancelamento', types: [], placeholder: '', title: 'Motivo quando a Entrada está Cancelada (#MT-6BF7122)' },
	];

	let operations = [
		{ hash: '#inventory-input',		label: 'Conferência&nbsp;de&nbsp;Entrada' 	},
		{ hash: '#submit-putaway',		label: 'Endereçamento&nbsp;de&nbsp;Lotes' 	},
		{ hash: '#create-invoices',		label: 'Gerar&nbsp;Notas&nbsp;Fiscais'		},
		{ hash: '#cancel-inputs-batch',	label: 'Cancelar&nbsp;em&nbsp;lote'			},
		{ hash: '#reverse-input',		label: 'Estornar&nbsp;Entrada'				},
		{ hash: '#input-xml-rastro',	label: 'Rastreabilidade&nbsp;XML'				},
		{ hash: '#input-divergences',	label: 'Divergências&nbsp;conferência'			},
		{ hash: '#input-reparse-rastro',	label: 'Reprocessar&nbsp;RASTRO'			},
	];

	let reports = [
		{ hash: '#print-inputs',		label: 'Listagem&nbsp;de&nbsp;Entradas' 			},
		{ hash: '#export-list-csv',		label: 'Exportar&nbsp;listagem&nbsp;(CSV)'		},
		{ hash: '#print-skus',			label: 'Cruzamento&nbsp;de&nbsp;Nota&nbsp;Fiscal'	},
		{ hash: '#print-xml', 			label: 'Espelho&nbsp;da&nbsp;Nota&nbsp;Fiscal' 		},
		{ hash: '#print-putaway', 		label: 'Ordem&nbsp;de&nbsp;Endereçamento' 			},
		{ hash: '#print-labels',		label: 'Etiquetas&nbsp;de&nbsp;Lotes' 				},
		{ hash: '#print-costs',			label: 'Relatório&nbsp;de&nbsp;Custos' 				},
		{ hash: '#print-conference',	label: 'Relatório&nbsp;de&nbsp;Conferência' 		},
		{ hash: '#print-details',		label: 'Entrada&nbsp;Detalhada' 					},
		{ hash: '#print-inputs-complete',	label: 'Relatório&nbsp;completo&nbsp;(status&nbsp;/&nbsp;NF)' },
		{ hash: '#print-inputs-documents-list',	label: 'Lista&nbsp;Doc.&nbsp;Entradas' },
	];

	router.set('#inventory-input',		inputs_crud.inventory.bind(inputs_crud));
	router.set('#submit-putaway', 		inputs_crud.putAway.bind(inputs_crud));
	router.set('#create-invoices', 		inputs_crud.createInvoices.bind(inputs_crud));
	router.set('#cancel-inputs-batch',	inputs_crud.cancelBatch.bind(inputs_crud));
	router.set('#reverse-input', 		inputs_crud.reverseInput.bind(inputs_crud));
	router.set('#input-xml-rastro', async () => {
		await showInputJson('Rastreabilidade XML esperada', api.input_expected_traceability);
	});
	router.set('#input-divergences', async () => {
		await showInputJson('Divergências de conferência', api.input_conference_divergences);
	});
	router.set('#input-reparse-rastro', async () => {
		await showInputJson('Reprocessamento XML RASTRO', api.reparse_input_xml_rastro);
	});

	router.set('#print-inputs', 		inputs_crud.printInputs.bind(inputs_crud));
	router.set('#export-list-csv',		inputs_crud.exportListCsv.bind(inputs_crud));
	router.set('#print-skus', 			inputs_crud.printInputSKUs.bind(inputs_crud));
	router.set('#print-xml', 			inputs_crud.printXML.bind(inputs_crud));
	router.set('#print-putaway', 		inputs_crud.printPutAwayOrder.bind(inputs_crud));
	router.set('#print-labels', 		inputs_crud.printLabels.bind(inputs_crud));
	router.set('#print-costs', 			inputs_crud.printCosts.bind(inputs_crud));
	router.set('#print-conference', 	inputs_crud.printConference.bind(inputs_crud));
	router.set('#print-details', 		inputs_crud.printDetails.bind(inputs_crud));

	async function showInputJson(title, endpoint)
	{
		const ids = await inputs_crud.getSelectedIds(['company_id', 'id']);
		if (ids.length !== 1) {
			notify('Selecione uma entrada.', title);
			return;
		}
		try {
			const data = await api.call(endpoint, ids[0][0], ids[0][1]);
			const text = JSON.stringify(data ?? {}, null, 2);
			notify(`<pre style="white-space:pre-wrap;max-width:70vw;max-height:70vh;overflow:auto;">${text.replace(/[&<>"']/g, (ch) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]))}</pre>`, title);
		} catch (e) {
			notify(e?.message || String(e), title);
		}
	}

	async function resolveCompanyIdForReport(raw) {
		if (!raw || String(raw).trim() === '') return '';
		const id = String(raw).trim();
		const c = await db.get('companies', id) || await db.get('companies', [id]);
		return c?.id ?? id;
	}

	async function buildInputsCompletePayload(formData) {
		const company_id = await resolveCompanyIdForReport(formData.company_id);
		const o = {
			company_id,
			date_from: formData.date_from,
			date_to: formData.date_to,
			date_basis: formData.date_basis || 'created',
			format: (formData.format || 'pdf').toLowerCase(),
		};
		if (formData.warehouse_id) o.warehouse_id = Number(formData.warehouse_id);
		if (formData.status) o.status = Number(formData.status);
		if (formData.order_type) o.order_type = formData.order_type;
		return o;
	}

	async function reloadWarehousesCompleteModal(companyRaw) {
		const sel = document.querySelector('#inputs-print-complete-report #warehouse_id');
		if (!sel) return;
		sel.innerHTML = '<option value="">Todos</option>';
		const company_id = await resolveCompanyIdForReport(companyRaw);
		if (!company_id) return;
		const data = await api.call(api.list_warehouses, `?filter=company_id*eq*${company_id}&limit=0&sort=2`);
		if (!Array.isArray(data)) return;
		for (const w of data) {
			const opt = document.createElement('option');
			opt.value = String(w.id);
			opt.textContent = `${w.name ?? ''} (${w.id})`;
			sel.appendChild(opt);
		}
	}

	function downloadInputsCompleteFile(base64Content, filename, mimeType) {
		const bin = atob(base64Content);
		const arr = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
		const blob = new Blob([arr], { type: mimeType });
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = filename || 'relatorio';
		a.click();
		URL.revokeObjectURL(a.href);
	}

	async function updateInputsCompleteCountMsg(getValues) {
		const el = document.querySelector('#ic-count-msg');
		if (!el) return;
		const formData = getValues?.() || {};
		if (!formData.date_from || !formData.date_to) {
			el.textContent = 'Informe as datas para estimar a quantidade de registros.';
			return;
		}
		const company_id = await resolveCompanyIdForReport(formData.company_id);
		if (!company_id) {
			el.textContent = 'Selecione a empresa para contar.';
			return;
		}
		const payload = await buildInputsCompletePayload(formData);
		delete payload.format;
		const resp = await api.call(api.count_inputs_complete_report, payload);
		const n = resp?.count;
		el.textContent = typeof n === 'number' ? `${n} entrada(s) no período com os filtros atuais.` : '—';
	}

	router.set('#print-inputs-complete', () => {
		const dlg = record.edit(print_complete_form, async (data) => {
			try {
				const payload = await buildInputsCompletePayload(data);
				const resp = await api.call(api.print_inputs_complete_report, payload);
				if (resp?.pdf) pdf(resp.pdf);
				else if (resp?.csv) {
					downloadInputsCompleteFile(resp.csv, resp.filename || 'entradas-completas.csv', 'text/csv;charset=utf-8');
					notify('Download iniciado.', 'Entradas');
				} else if (resp?.xlsx) {
					downloadInputsCompleteFile(resp.xlsx, resp.filename || 'entradas-completas.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
					notify('Download iniciado.', 'Entradas');
				} else notify(resp?.message || 'Nenhum dado para o relatório.', 'Entradas');
			} catch (e) {
				notify(e?.message || String(e), 'Erro');
			}
			dlg.destroy();
		}, {}, false);
		const formEl = document.querySelector(print_complete_form.form.selector);
		const refreshCount = () => updateInputsCompleteCountMsg(() => dlg.form?.getValues?.());
		if (formEl) {
			formEl.querySelector('#company_id')?.addEventListener('change', async () => {
				await reloadWarehousesCompleteModal(formEl.querySelector('#company_id')?.value);
				refreshCount();
			});
			formEl.addEventListener('change', refreshCount);
			formEl.addEventListener('input', refreshCount);
		}
		setTimeout(refreshCount, 300);
	});

	async function buildInputDocumentsListPayload(formData, formEl = null) {
		const company_id = await resolveCompanyIdForReport(formData.company_id);
		const root = formEl || document.querySelector('#inputs-print-documents-list');
		const empenhoCb = root?.querySelector('#empenho_only');
		const o = {
			company_id,
			date_from: formData.date_from,
			date_to: formData.date_to,
			date_basis: formData.date_basis || 'created',
			format: (formData.format || 'xlsx').toLowerCase(),
		};
		if (formData.warehouse_id) o.warehouse_id = Number(formData.warehouse_id);
		if (formData.status) o.status = Number(formData.status);
		if (empenhoCb ? empenhoCb.checked : formData.empenho_only) o.empenho_only = true;
		return o;
	}

	async function reloadWarehousesDocumentsListModal(companyRaw) {
		const sel = document.querySelector('#inputs-print-documents-list #warehouse_id');
		if (!sel) return;
		sel.innerHTML = '<option value="">Todos</option>';
		const company_id = await resolveCompanyIdForReport(companyRaw);
		if (!company_id) return;
		const data = await api.call(api.list_warehouses, `?filter=company_id*eq*${company_id}&limit=0&sort=2`);
		if (!Array.isArray(data)) return;
		for (const w of data) {
			const opt = document.createElement('option');
			opt.value = String(w.id);
			opt.textContent = `${w.name ?? ''} (${w.id})`;
			sel.appendChild(opt);
		}
	}

	async function updateInputDocumentsListCountMsg(getValues) {
		const el = document.querySelector('#idl-count-msg');
		if (!el) return;
		const formData = getValues?.() || {};
		if (!formData.date_from || !formData.date_to) {
			el.textContent = 'Informe as datas para estimar a quantidade de registros.';
			return;
		}
		const company_id = await resolveCompanyIdForReport(formData.company_id);
		if (!company_id) {
			el.textContent = 'Selecione a empresa para contar.';
			return;
		}
		const formEl = document.querySelector('#inputs-print-documents-list');
		const payload = await buildInputDocumentsListPayload(formData, formEl);
		delete payload.format;
		const resp = await api.call(api.count_input_documents_list_report, payload);
		const n = resp?.count;
		el.textContent = typeof n === 'number'
			? (n === 0
				? 'Nenhuma entrada no período. Ajuste as datas, a base do período ou desmarque «Somente com empenho informado».'
				: `${n} entrada(s) no período com os filtros atuais.`)
			: '—';
	}

	router.set('#print-inputs-documents-list', () => {
		const dlg = record.edit(print_documents_list_form, async (data) => {
			const formEl = document.querySelector(print_documents_list_form.form.selector);
			try {
				const formatSel = formEl?.querySelector('#format');
				const payload = await buildInputDocumentsListPayload(data, formEl);
				if (formatSel?.value) payload.format = String(formatSel.value).toLowerCase();
				const countPayload = { ...payload };
				delete countPayload.format;
				const cntResp = await api.call(api.count_input_documents_list_report, countPayload);
				if (cntResp?.count === 0) {
					notify(
						'Nenhuma entrada no período com os filtros informados. Ajuste as datas, a base do período ou desmarque «Somente com empenho informado».',
						'Lista Doc. Entradas',
					);
					return;
				}
				const resp = await api.call(api.print_input_documents_list_report, payload);
				if (resp?.xlsx) {
					downloadInputsCompleteFile(resp.xlsx, resp.filename || 'lista-doc-entradas.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
					notify('Download Excel iniciado.', 'Lista Doc. Entradas');
				} else if (resp?.csv) {
					downloadInputsCompleteFile(resp.csv, resp.filename || 'lista-doc-entradas.csv', 'text/csv;charset=utf-8');
					notify('Download CSV iniciado.', 'Lista Doc. Entradas');
				} else if (resp?.pdf) pdf(resp.pdf);
				else notify(resp?.message || 'Nenhum dado para o relatório.', 'Lista Doc. Entradas');
				dlg.destroy();
			} catch (e) {
				notify(e?.message || String(e), 'Erro');
			}
		}, {}, false);
		const formEl = document.querySelector(print_documents_list_form.form.selector);
		const refreshCount = () => updateInputDocumentsListCountMsg(() => dlg.form?.getValues?.());
		if (formEl) {
			formEl.querySelector('#company_id')?.addEventListener('change', async () => {
				await reloadWarehousesDocumentsListModal(formEl.querySelector('#company_id')?.value);
				refreshCount();
			});
			formEl.addEventListener('change', refreshCount);
			formEl.addEventListener('input', refreshCount);
		}
		setTimeout(refreshCount, 300);
	});

	inputs_crud.run(
		'inputs',
		'Entradas',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports,
		{ default_columns: '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,17,29,18,20,21,22,23' }
	);

};

export default inputs;

